/**
 * Reducer del LinkPaymentsModal.
 *
 * Estado:
 *   phase: 'loading'|'ready'|'saving'|'done'|'error'
 *   error: string|null   — mensaje del fetch inicial
 *   enrollments: Array<{id, status, createdAt, semesterName, programName, isSemesterBased}>
 *   subjectCount: number  — total de materias del programa (rango de ordinales)
 *   cards: {[paymentId]: {payment, enrollmentId, ordinals: number[], months: number[], included: bool, status: 'pending'|'saved'|'failed', error?: {code,message,conflict?}}}
 *   order: paymentId[]   — orden de render (respeta fecha del backend)
 *
 * Cross-blocking: selectores getReservedOrdinalsExcludingCard /
 * getReservedMonthsExcludingCard leen el resto de las cards y devuelven
 * Map<value, paymentId> con lo que ya está tomado por peers dentro del modal.
 *
 * NO se hace fetch dentro del reducer — llega listo desde useLinkPaymentsData.
 */

export const initialState = {
  phase: 'loading',
  error: null,
  enrollments: [],
  subjectCount: 0,
  // {[enrollmentId]: Map<ordinal, paymentId>} — ordinales ya tomados en BD
  // por pagos de esa matrícula. Se pinta como tachado en OrdinalsPicker.
  claimedOrdinalsByEnrollment: {},
  meta: { enrollmentType: null, studentId: null, programId: null, isSemesterBased: false },
  cards: {},
  order: [],
};

export function init() {
  return initialState;
}

export function reducer(state, action) {
  switch (action.type) {
    case 'LOAD_START':
      return { ...initialState, phase: 'loading' };

    case 'LOAD_SUCCESS': {
      const { enrollments, payments, meta, claimedOrdinalsByEnrollment } = action.payload;
      const cards = {};
      const order = [];
      for (const p of payments) {
        const isLinked = p.linked === true;
        const savedSkip = p.does_not_cover_subjects === true;
        cards[p.id] = {
          payment: p,
          enrollmentId: p.currentEnrollmentId || p.suggestedEnrollmentId || (enrollments[0]?.id ?? null),
          // Pagos ya vinculados llegan con sus cantidades pintadas.
          ordinals: isLinked && !savedSkip ? [...(p.paid_subject_numbers || [])] : [],
          months: [],
          // "No cubre materias" persistido en BD: se refleja como toggle activo.
          skipOrdinals: savedSkip,
          // Los vinculados no se re-envían con el batch (ya están en BD).
          included: !isLinked,
          status: isLinked ? 'saved' : 'pending',
          error: null,
        };
        order.push(p.id);
      }
      const isSemesterBased = enrollments.some((e) => e.isSemesterBased);
      // Normaliza claimedOrdinalsByEnrollment del backend ({eid: [{ordinal,paymentId}]})
      // a {eid: Map<ordinal, paymentId>} para lectura O(1) en el picker.
      const claimedMap = {};
      for (const [eid, list] of Object.entries(claimedOrdinalsByEnrollment || {})) {
        const m = new Map();
        for (const c of (Array.isArray(list) ? list : [])) {
          if (Number.isInteger(c?.ordinal)) m.set(c.ordinal, { paymentId: c.paymentId ?? null, source: c.source || 'payment' });
        }
        claimedMap[eid] = m;
      }
      return {
        ...state,
        phase: 'ready',
        error: null,
        enrollments,
        subjectCount: meta.subjectCount || 0,
        claimedOrdinalsByEnrollment: claimedMap,
        meta: { ...meta, isSemesterBased },
        cards,
        order,
      };
    }

    case 'LOAD_ERROR':
      return { ...state, phase: 'error', error: action.error || 'Error al cargar' };

    case 'SELECT_CANDIDATE': {
      const { paymentId, enrollmentId } = action;
      const card = state.cards[paymentId];
      if (!card) return state;
      // Cambiar de matrícula limpia ordinales, meses y skipOrdinals (los peers cambian).
      return {
        ...state,
        cards: {
          ...state.cards,
          [paymentId]: { ...card, enrollmentId, ordinals: [], months: [], skipOrdinals: false, status: 'pending', error: null },
        },
      };
    }

    case 'TOGGLE_SKIP_ORDINALS': {
      const { paymentId } = action;
      const card = state.cards[paymentId];
      if (!card) return state;
      const nextSkip = !card.skipOrdinals;
      // Marcar skipOrdinals limpia los ordinales seleccionados (mutuamente excluyentes).
      return {
        ...state,
        cards: {
          ...state.cards,
          [paymentId]: { ...card, skipOrdinals: nextSkip, ordinals: nextSkip ? [] : card.ordinals },
        },
      };
    }

    case 'TOGGLE_ORDINAL': {
      const { paymentId, ordinal } = action;
      const card = state.cards[paymentId];
      if (!card) return state;
      const has = card.ordinals.includes(ordinal);
      const nextOrdinals = has
        ? card.ordinals.filter((n) => n !== ordinal)
        : [...card.ordinals, ordinal].sort((a, b) => a - b);
      return {
        ...state,
        cards: { ...state.cards, [paymentId]: { ...card, ordinals: nextOrdinals } },
      };
    }

    case 'TOGGLE_MONTH': {
      const { paymentId, monthId } = action;
      const card = state.cards[paymentId];
      if (!card) return state;
      const has = card.months.includes(monthId);
      const nextMonths = has
        ? card.months.filter((n) => n !== monthId)
        : [...card.months, monthId].sort((a, b) => a - b);
      return {
        ...state,
        cards: { ...state.cards, [paymentId]: { ...card, months: nextMonths } },
      };
    }

    case 'TOGGLE_INCLUDE': {
      const { paymentId } = action;
      const card = state.cards[paymentId];
      if (!card) return state;
      return {
        ...state,
        cards: { ...state.cards, [paymentId]: { ...card, included: !card.included } },
      };
    }

    case 'UNLINK_PAYMENT': {
      // Se aplicó DELETE /payments/manual-link/:id: la card vuelve a estar pending
      // (huérfana) y se liberan sus cantidades del claimedOrdinalsByEnrollment.
      const { paymentId } = action;
      const card = state.cards[paymentId];
      if (!card) return state;
      const eid = card.enrollmentId;
      const nextClaimed = { ...state.claimedOrdinalsByEnrollment };
      if (eid != null && nextClaimed[eid]) {
        const m = new Map(nextClaimed[eid]);
        for (const [ord, entry] of m.entries()) {
          const pid = entry && typeof entry === 'object' ? entry.paymentId : entry;
          if (Number(pid) === Number(paymentId)) m.delete(ord);
        }
        nextClaimed[eid] = m;
      }
      return {
        ...state,
        claimedOrdinalsByEnrollment: nextClaimed,
        cards: {
          ...state.cards,
          [paymentId]: {
            ...card,
            ordinals: [],
            months: [],
            skipOrdinals: false,
            included: true,
            status: 'pending',
            error: null,
          },
        },
      };
    }

    case 'BEGIN_PERSIST':
      return { ...state, phase: 'saving' };

    case 'PERSIST_ONE_SUCCESS': {
      const { paymentId } = action;
      const card = state.cards[paymentId];
      if (!card) return state;
      return {
        ...state,
        cards: { ...state.cards, [paymentId]: { ...card, status: 'saved', error: null } },
      };
    }

    case 'PERSIST_ONE_FAILURE': {
      const { paymentId, code, message, conflict } = action;
      const card = state.cards[paymentId];
      if (!card) return state;
      return {
        ...state,
        cards: {
          ...state.cards,
          [paymentId]: { ...card, status: 'failed', error: { code, message, conflict } },
        },
      };
    }

    case 'PERSIST_DONE':
      return { ...state, phase: 'done' };

    case 'RESET_FAILED_ONLY': {
      // Marca todos los failed de vuelta a pending para reintentar.
      const cards = { ...state.cards };
      for (const id of Object.keys(cards)) {
        if (cards[id].status === 'failed') {
          cards[id] = { ...cards[id], status: 'pending', error: null };
        }
      }
      return { ...state, phase: 'ready', cards };
    }

    case 'RESET':
      return init();

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Selectores (puros, se llaman desde los componentes con el state)
// ---------------------------------------------------------------------------

/**
 * Devuelve Map<ordinal, paymentIdPeer> de los ordinales ya seleccionados por
 * OTRAS cards para el MISMO enrollment. Los ordinales del propio card no se
 * bloquean.
 */
export function getReservedOrdinalsExcludingCard(state, currentPaymentId) {
  const currentCard = state.cards[currentPaymentId];
  const enrollmentId = currentCard?.enrollmentId ?? null;
  const reserved = new Map();
  if (enrollmentId == null) return reserved;
  for (const [id, card] of Object.entries(state.cards)) {
    if (Number(id) === Number(currentPaymentId)) continue;
    if (!card.included) continue;
    if (card.status === 'saved' || card.status === 'failed') {
      // Los saved ya viven en BD; los failed pueden reintentarse pero mientras
      // tanto su ordinal ya no está reservado por el modal (queda en BD si el
      // fallo fue en otro punto). Simplificación: no bloqueamos por failed.
      continue;
    }
    if (card.enrollmentId !== enrollmentId) continue;
    for (const o of card.ordinals) {
      if (!reserved.has(o)) reserved.set(o, Number(id));
    }
  }
  return reserved;
}

export function getReservedMonthsExcludingCard(state, currentPaymentId) {
  const currentCard = state.cards[currentPaymentId];
  const enrollmentId = currentCard?.enrollmentId ?? null;
  const reserved = new Map();
  if (enrollmentId == null) return reserved;
  for (const [id, card] of Object.entries(state.cards)) {
    if (Number(id) === Number(currentPaymentId)) continue;
    if (!card.included) continue;
    if (card.status === 'saved' || card.status === 'failed') continue;
    if (card.enrollmentId !== enrollmentId) continue;
    for (const m of card.months) {
      if (!reserved.has(m)) reserved.set(m, Number(id));
    }
  }
  return reserved;
}
